import React, { useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowLeft, CheckCircle2, Users, Ticket, MessageSquare } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillPhone = params.get("phone") || "";
  const prefillName = params.get("name") || "";
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: prefillName, company: "", phone: prefillPhone, email: "",
    password: "", confirm: "", terms: false,
  });

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("A senha precisa ter no mínimo 6 caracteres");
    if (form.password !== form.confirm) return toast.error("As senhas não coincidem");
    if (!form.terms) return toast.error("Você precisa aceitar os termos");
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, {
        phone: form.phone || undefined,
        company: form.company || undefined,
      });
      toast.success("Conta criada! Bem-vindo(a).");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Ticket, title: "Abra chamados 24/7", desc: "Reporte problemas e acompanhe o andamento em tempo real." },
    { icon: Users, title: "Acesso à equipe técnica", desc: "Interaja com técnicos que resolvem seu chamado com histórico completo." },
    { icon: MessageSquare, title: "Base de conhecimento", desc: "Consulte soluções e documentação antes mesmo de abrir um chamado." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/login" className="flex items-center gap-2" data-testid="brand-link">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="font-heading text-lg font-semibold tracking-tight">HelpDesk</div>
          </Link>
          <Link to="/login">
            <Button variant="ghost" size="sm" data-testid="back-to-login-btn">
              <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao login
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-6 py-12 lg:grid-cols-5 lg:py-16">
        {/* Left: pitch */}
        <div className="space-y-8 lg:col-span-2">
          <div>
            <div className="mb-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">Cadastro de Cliente</div>
            <h1 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl">
              Crie sua conta e comece a abrir chamados em minutos.
            </h1>
            <p className="mt-4 text-base text-muted-foreground">
              Registre-se gratuitamente como <b>Cliente</b>. Sua conta é ativada imediatamente e você já pode reportar problemas para nossa equipe.
            </p>
          </div>

          <ul className="space-y-5">
            {benefits.map((b) => (
              <li key={b.title} className="flex gap-4">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border">
                  <b.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-medium">{b.title}</div>
                  <div className="text-sm text-muted-foreground">{b.desc}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Sem cartão de crédito. Sem prazo. Cancele quando quiser.
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-3">
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Preencha seus dados</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Já tem uma conta? <Link to="/login" className="font-medium text-foreground underline underline-offset-4">Entrar</Link>
                </p>
                {prefillPhone && (
                  <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                    Estamos vinculando o número <b>+{prefillPhone}</b> que entrou em contato via WhatsApp.
                  </div>
                )}
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="register-form">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="r-name">Nome completo *</Label>
                    <Input id="r-name" required value={form.name}
                      onChange={update("name")} placeholder="João da Silva"
                      data-testid="reg-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="r-company">Empresa</Label>
                    <Input id="r-company" value={form.company}
                      onChange={update("company")} placeholder="Acme LTDA (opcional)"
                      data-testid="reg-company-input" />
                  </div>
                  <div>
                    <Label htmlFor="r-email">E-mail *</Label>
                    <Input id="r-email" type="email" required value={form.email}
                      onChange={update("email")} placeholder="voce@empresa.com"
                      data-testid="reg-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="r-phone">Telefone</Label>
                    <Input id="r-phone" type="tel" value={form.phone}
                      onChange={update("phone")} placeholder="(11) 99999-9999"
                      data-testid="reg-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="r-pass">Senha *</Label>
                    <Input id="r-pass" type="password" required minLength={6}
                      value={form.password} onChange={update("password")}
                      placeholder="Mínimo 6 caracteres"
                      data-testid="reg-password-input" />
                  </div>
                  <div>
                    <Label htmlFor="r-confirm">Confirmar senha *</Label>
                    <Input id="r-confirm" type="password" required minLength={6}
                      value={form.confirm} onChange={update("confirm")}
                      placeholder="Repita a senha"
                      data-testid="reg-confirm-input" />
                  </div>
                </div>

                <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
                  <Checkbox
                    checked={form.terms}
                    onCheckedChange={(v) => setForm({ ...form, terms: !!v })}
                    className="mt-0.5"
                    data-testid="reg-terms-checkbox"
                  />
                  <span className="leading-relaxed text-muted-foreground">
                    Li e aceito os <a href="#" className="text-foreground underline underline-offset-4">Termos de Uso</a> e a{" "}
                    <a href="#" className="text-foreground underline underline-offset-4">Política de Privacidade</a>.
                  </span>
                </label>

                <Button type="submit" disabled={loading} className="w-full" data-testid="reg-submit-btn">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Criando conta...</> : "Criar conta gratuita"}
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  Sua conta será criada com perfil <b>Cliente</b>. Para virar técnico ou administrador, contate seu gestor.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
